// frontend/src/contexts/AppContext.js
import React, { createContext, useContext, useReducer, useMemo } from 'react';

// Начальное состояние приложения
const initialState = {
    // Пользователь
    user: {
        username: '',
        id: null,
        email: '',
        isHost: false,
        isAuthenticated: false
    },
    auth: {
        status: 'loading', // loading | guest | authenticated
        profile: null,
        error: null
    },
    
    // Комната
    room: {
        id: null,
        state: null,
        step: 0 // 0: аутентификация, 1: имя, 2: выбор комнаты, 3: в комнате
    },
    
    // Чат
    chat: {
        messages: [],
        newMessage: '',
        isSending: false,
        pendingMessages: new Map()
    },
    
    // Видео
    video: {
        queue: [],
        currentIndex: -1,
        currentVideo: null,
        showQueue: false,
        isAddingVideo: false,
        newVideoUrl: ''
    },
    
    // Аудио/WebRTC
    audio: {
        isMicMuted: true,
        voiceChatEnabled: false,
        remoteStreams: new Map()
    },
      // UI состояние
    ui: {
        error: '',
        inputRoomId: '',
        showAddUrlForm: false,
        showConfirmModal: false,
        confirmModalProps: {
            title: '',
            message: '',
            onConfirm: () => {},
            confirmText: 'OK',
            cancelText: 'Отмена',
            confirmButtonType: 'danger'
        }
    }
};

// Action типы
export const ACTION_TYPES = {
    // Пользователь
    SET_USERNAME: 'SET_USERNAME',
    SET_USER_ID: 'SET_USER_ID',
    SET_IS_HOST: 'SET_IS_HOST',
    SET_AUTH_STATUS: 'SET_AUTH_STATUS',
    SET_AUTH_USER: 'SET_AUTH_USER',
    RESET_APP_STATE: 'RESET_APP_STATE',
    
    // Комната
    SET_ROOM_ID: 'SET_ROOM_ID',
    SET_ROOM_STATE: 'SET_ROOM_STATE',
    SET_STEP: 'SET_STEP',
    LEAVE_ROOM: 'LEAVE_ROOM',
    
    // Чат
    SET_NEW_MESSAGE: 'SET_NEW_MESSAGE',
    SET_IS_SENDING: 'SET_IS_SENDING',
    ADD_MESSAGE: 'ADD_MESSAGE',
    ADD_PENDING_MESSAGE: 'ADD_PENDING_MESSAGE',
    REMOVE_PENDING_MESSAGE: 'REMOVE_PENDING_MESSAGE',
    CLEAR_PENDING_MESSAGES: 'CLEAR_PENDING_MESSAGES',
    UPDATE_MESSAGE_STATUS: 'UPDATE_MESSAGE_STATUS',
    
    // Видео
    SET_VIDEO_QUEUE: 'SET_VIDEO_QUEUE',
    SET_CURRENT_VIDEO_INDEX: 'SET_CURRENT_VIDEO_INDEX',
    SET_SHOW_VIDEO_QUEUE: 'SET_SHOW_VIDEO_QUEUE',
    SET_IS_ADDING_VIDEO: 'SET_IS_ADDING_VIDEO',
    SET_NEW_VIDEO_URL: 'SET_NEW_VIDEO_URL',
    
    // Аудио
    SET_MIC_MUTED: 'SET_MIC_MUTED',
    SET_VOICE_CHAT_ENABLED: 'SET_VOICE_CHAT_ENABLED',
    SET_REMOTE_STREAMS: 'SET_REMOTE_STREAMS',
    ADD_REMOTE_STREAM: 'ADD_REMOTE_STREAM',
    REMOVE_REMOTE_STREAM: 'REMOVE_REMOTE_STREAM',
      // UI
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_INPUT_ROOM_ID: 'SET_INPUT_ROOM_ID',
    SET_SHOW_ADD_URL_FORM: 'SET_SHOW_ADD_URL_FORM',
    SET_SHOW_CONFIRM_MODAL: 'SET_SHOW_CONFIRM_MODAL',
    SET_CONFIRM_MODAL_PROPS: 'SET_CONFIRM_MODAL_PROPS'
};

// Reducer функция
const appReducer = (state, action) => {
    switch (action.type) {
        case ACTION_TYPES.SET_USERNAME:
            return {
                ...state,
                user: { ...state.user, username: action.payload }
            };
            
        case ACTION_TYPES.SET_USER_ID:
            return {
                ...state,
                user: { ...state.user, id: action.payload }
            };
            
        case ACTION_TYPES.SET_IS_HOST:
            return {
                ...state,
                user: { ...state.user, isHost: action.payload }
            };

        case ACTION_TYPES.SET_AUTH_STATUS: {
            const nextStatus = action.payload?.status ?? state.auth.status;
            return {
                ...state,
                auth: {
                    ...state.auth,
                    status: nextStatus,
                    error: action.payload?.error ?? null
                },
                user: nextStatus === 'authenticated'
                    ? state.user
                    : {
                        ...state.user,
                        isAuthenticated: false
                    }
            };
        }

        case ACTION_TYPES.SET_AUTH_USER: {
            const profile = action.payload || null;
            return {
                ...state,
                auth: {
                    ...state.auth,
                    profile,
                    status: profile ? 'authenticated' : 'guest',
                    error: null
                },
                user: {
                    ...state.user,
                    id: profile?.id ?? null,
                    username: profile?.username ?? state.user.username,
                    email: profile?.email ?? '',
                    isAuthenticated: Boolean(profile),
                    isHost: Boolean(profile?.isHost || profile?.role === 'host')
                }
            };
        }

        case ACTION_TYPES.RESET_APP_STATE:
            return {
                ...initialState,
                auth: {
                    status: action.payload?.authStatus ?? 'guest',
                    profile: null,
                    error: null
                }
            };
            
        case ACTION_TYPES.SET_ROOM_ID:
            return {
                ...state,
                room: { ...state.room, id: action.payload }
            };
            
        case ACTION_TYPES.SET_ROOM_STATE:
            console.log('🏪 [CONTEXT] SET_ROOM_STATE:', {
                previousState: state.room.state,
                newState: action.payload,
                videoQueue: action.payload?.videoQueue?.length,
                currentIndex: action.payload?.currentVideoIndex
            });
            return {
                ...state,
                room: { ...state.room, state: action.payload }
            };
            
        case ACTION_TYPES.SET_STEP:
            return {
                ...state,
                room: { ...state.room, step: action.payload }
            };
            
        case ACTION_TYPES.LEAVE_ROOM:
            return {
                ...state,
                room: { ...state.room, id: null, state: null, step: 2 },
                chat: { ...initialState.chat },
                video: { ...initialState.video },
                audio: { ...initialState.audio, voiceChatEnabled: false },
                ui: { ...state.ui, error: '', showAddUrlForm: false }
            };
            
        case ACTION_TYPES.SET_NEW_MESSAGE:
            return {
                ...state,
                chat: { ...state.chat, newMessage: action.payload }
            };
            
        case ACTION_TYPES.SET_IS_SENDING:
            return {
                ...state,
                chat: { ...state.chat, isSending: action.payload }
            };
            
        case ACTION_TYPES.ADD_MESSAGE: {
            const incoming = action.payload;
            const exists = incoming?.id && state.chat.messages.some(msg => msg.id === incoming.id);
            if (exists) {
                return state;
            }

            return {
                ...state,
                chat: {
                    ...state.chat,
                    messages: [...state.chat.messages, incoming]
                }
            };
        }

        case ACTION_TYPES.ADD_PENDING_MESSAGE:
            return {
                ...state,
                chat: {
                    ...state.chat,
                    pendingMessages: (() => {
                        const newMap = new Map(state.chat.pendingMessages);
                        const payload = {
                            ...action.payload,
                            isPending: true
                        };
                        newMap.set(payload.id, payload);
                        console.log(`📝 [AppContext] ADD_PENDING_MESSAGE:`, {
                            messageId: payload.id,
                            messageText: payload.text,
                            sizeBefore: state.chat.pendingMessages.size,
                            sizeAfter: newMap.size,
                            allKeys: Array.from(newMap.keys())
                        });
                        return newMap;
                    })()
                }
            };

        case ACTION_TYPES.REMOVE_PENDING_MESSAGE:
            return {
                ...state,
                chat: {
                    ...state.chat,
                    pendingMessages: (() => {
                        const newMap = new Map(state.chat.pendingMessages);
                        const wasDeleted = newMap.delete(action.payload); // action.payload это уже id
                        console.log(`🗂️ [AppContext] REMOVE_PENDING_MESSAGE:`, {
                            keyToRemove: action.payload,
                            wasDeleted,
                            sizeBefore: state.chat.pendingMessages.size,
                            sizeAfter: newMap.size,
                            keysRemaining: Array.from(newMap.keys())
                        });
                        return newMap;
                    })()
                }
            };
            
        case ACTION_TYPES.CLEAR_PENDING_MESSAGES:
            return {
                ...state,
                chat: { ...state.chat, pendingMessages: new Map() }
            };

        case ACTION_TYPES.UPDATE_MESSAGE_STATUS: {
            const { messageId, status } = action.payload || {};
            if (!messageId || !status) {
                return state;
            }

            const updatedMessages = state.chat.messages.map(message => {
                if (message.id !== messageId) return message;
                return {
                    ...message,
                    status
                };
            });

            return {
                ...state,
                chat: {
                    ...state.chat,
                    messages: updatedMessages
                }
            };
        }
            
        case ACTION_TYPES.SET_MIC_MUTED:
            return {
                ...state,
                audio: { ...state.audio, isMicMuted: action.payload }
            };
            
        case ACTION_TYPES.SET_VOICE_CHAT_ENABLED:
            return {
                ...state,
                audio: { ...state.audio, voiceChatEnabled: action.payload }
            };
            
        case ACTION_TYPES.ADD_REMOTE_STREAM:
            return {
                ...state,
                audio: {
                    ...state.audio,
                    remoteStreams: (() => {
                        const newMap = new Map(state.audio.remoteStreams);
                        newMap.set(action.payload.id, action.payload.stream);
                        return newMap;
                    })()
                }
            };
            
        case ACTION_TYPES.REMOVE_REMOTE_STREAM:
            return {
                ...state,
                audio: {
                    ...state.audio,
                    remoteStreams: (() => {
                        const newMap = new Map(state.audio.remoteStreams);
                        newMap.delete(action.payload);
                        return newMap;
                    })()
                }
            };
            
        case ACTION_TYPES.SET_ERROR:
            return {
                ...state,
                ui: { ...state.ui, error: action.payload }
            };
              case ACTION_TYPES.CLEAR_ERROR:
            return {
                ...state,
                ui: { ...state.ui, error: '' }
            };
            
        case ACTION_TYPES.SET_INPUT_ROOM_ID:
            return {
                ...state,
                ui: { ...state.ui, inputRoomId: action.payload }
            };
            
        case ACTION_TYPES.SET_SHOW_ADD_URL_FORM:
            return {
                ...state,
                ui: { ...state.ui, showAddUrlForm: action.payload }
            };
            
        case ACTION_TYPES.SET_SHOW_CONFIRM_MODAL:
            return {
                ...state,
                ui: { ...state.ui, showConfirmModal: action.payload }
            };
              case ACTION_TYPES.SET_CONFIRM_MODAL_PROPS:
            return {
                ...state,
                ui: { ...state.ui, confirmModalProps: action.payload }
            };
            
        // Видео действия
        case ACTION_TYPES.SET_SHOW_VIDEO_QUEUE:
            return {
                ...state,
                video: { ...state.video, showQueue: action.payload }
            };
            
        case ACTION_TYPES.SET_NEW_VIDEO_URL:
            return {
                ...state,
                video: { ...state.video, newVideoUrl: action.payload }
            };
            
        case ACTION_TYPES.SET_IS_ADDING_VIDEO:
            return {
                ...state,
                video: { ...state.video, isAddingVideo: action.payload }
            };
            
        default:
            return state;
    }
};

// Context
const AppContext = createContext();

// Provider компонент
export const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);
    
    // Мемоизированное значение контекста
    const contextValue = useMemo(() => ({
        state,
        dispatch
    }), [state]);
    
    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
};

// Хук для использования контекста
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider');
    }
    return context;
};

// Селекторы для удобного доступа к данным
// DEPRECATED: Используйте гранулярные селекторы ниже
export const useAppSelectors = () => {
    const { state } = useAppContext();
    
    return useMemo(() => ({
        // Пользователь
        username: state.user.username,
        userId: state.user.id,
        isHost: state.user.isHost,
        
        // Комната
        roomId: state.room.id,
        roomState: state.room.state,
        step: state.room.step,
        
        // Чат
        chatMessages: state.chat.messages,
        newMessage: state.chat.newMessage,
        isSending: state.chat.isSending,
        pendingMessages: state.chat.pendingMessages,
          // Видео
        videoQueue: state.video.queue,
        currentVideoIndex: state.video.currentIndex,
        currentVideo: state.video.currentVideo,
        showVideoQueue: state.video.showQueue,
        newVideoUrl: state.video.newVideoUrl,
        isAddingVideo: state.video.isAddingVideo,
        
        // Аудио
        isMicMuted: state.audio.isMicMuted,
        voiceChatEnabled: state.audio.voiceChatEnabled,
        remoteStreams: state.audio.remoteStreams,
          // UI
        error: state.ui.error,
        inputRoomId: state.ui.inputRoomId,
        showAddUrlForm: state.ui.showAddUrlForm,
        showConfirmModal: state.ui.showConfirmModal,
        confirmModalProps: state.ui.confirmModalProps
    }), [state]);
};

// Новые гранулярные селекторы
export const useUserSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        username: state.user.username,
        userId: state.user.id,
        isHost: state.user.isHost,
        isAuthenticated: state.user.isAuthenticated,
        email: state.user.email,
    }), [state.user]);
};

export const useAuthSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        status: state.auth.status,
        profile: state.auth.profile,
        error: state.auth.error,
        loading: state.auth.status === 'loading',
        isAuthenticated: state.auth.status === 'authenticated'
    }), [state.auth]);
};

export const useRoomSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        roomId: state.room.id,
        roomState: state.room.state,
        step: state.room.step,
    }), [state.room]);
};

export const useChatSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        chatMessages: state.chat.messages,
        newMessage: state.chat.newMessage,
        isSending: state.chat.isSending,
        pendingMessages: state.chat.pendingMessages,
    }), [state.chat]);
};

export const useVideoSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        videoQueue: state.video.queue,
        currentVideoIndex: state.video.currentIndex,
        currentVideo: state.video.currentVideo,
        showVideoQueue: state.video.showQueue,
        newVideoUrl: state.video.newVideoUrl,
        isAddingVideo: state.video.isAddingVideo,
    }), [state.video]);
};

export const useUISelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        error: state.ui.error,
        inputRoomId: state.ui.inputRoomId,
        showAddUrlForm: state.ui.showAddUrlForm,
        showConfirmModal: state.ui.showConfirmModal,
        confirmModalProps: state.ui.confirmModalProps,
    }), [state.ui]);
};

export const useAudioSelectors = () => {
    const { state } = useAppContext();
    return useMemo(() => ({
        isMicMuted: state.audio.isMicMuted,
        voiceChatEnabled: state.audio.voiceChatEnabled,
        remoteStreams: state.audio.remoteStreams,
    }), [state.audio]);
};

// Action creators
export const useAppActions = () => {
    const { dispatch } = useAppContext();
    
    return useMemo(() => ({
        // Пользователь
        setUsername: (username) => dispatch({ type: ACTION_TYPES.SET_USERNAME, payload: username }),
        setUserId: (id) => dispatch({ type: ACTION_TYPES.SET_USER_ID, payload: id }),
        setIsHost: (isHost) => dispatch({ type: ACTION_TYPES.SET_IS_HOST, payload: isHost }),
    setAuthStatus: (payload) => dispatch({ type: ACTION_TYPES.SET_AUTH_STATUS, payload }),
    setAuthUser: (profile) => dispatch({ type: ACTION_TYPES.SET_AUTH_USER, payload: profile }),
    resetAppState: (options) => dispatch({ type: ACTION_TYPES.RESET_APP_STATE, payload: options }),
        
        // Комната
        setRoomId: (roomId) => dispatch({ type: ACTION_TYPES.SET_ROOM_ID, payload: roomId }),
        setRoomState: (roomState) => dispatch({ type: ACTION_TYPES.SET_ROOM_STATE, payload: roomState }),
        setStep: (step) => dispatch({ type: ACTION_TYPES.SET_STEP, payload: step }),
        leaveRoom: () => dispatch({ type: ACTION_TYPES.LEAVE_ROOM }),
        
        // Чат
        setNewMessage: (message) => dispatch({ type: ACTION_TYPES.SET_NEW_MESSAGE, payload: message }),
        setIsSending: (isSending) => dispatch({ type: ACTION_TYPES.SET_IS_SENDING, payload: isSending }),
        addMessage: (message) => dispatch({ type: ACTION_TYPES.ADD_MESSAGE, payload: message }),
        addPendingMessage: (message) => dispatch({ type: ACTION_TYPES.ADD_PENDING_MESSAGE, payload: message }),
        removePendingMessage: (messageId) => dispatch({ type: ACTION_TYPES.REMOVE_PENDING_MESSAGE, payload: messageId }),
        clearPendingMessages: () => dispatch({ type: ACTION_TYPES.CLEAR_PENDING_MESSAGES }),
        updateMessageStatus: (messageId, status) => dispatch({ type: ACTION_TYPES.UPDATE_MESSAGE_STATUS, payload: { messageId, status } }),
          // Видео
        setShowVideoQueue: (show) => dispatch({ type: ACTION_TYPES.SET_SHOW_VIDEO_QUEUE, payload: show }),
        setNewVideoUrl: (url) => dispatch({ type: ACTION_TYPES.SET_NEW_VIDEO_URL, payload: url }),
        setIsAddingVideo: (isAdding) => dispatch({ type: ACTION_TYPES.SET_IS_ADDING_VIDEO, payload: isAdding }),
        
        // Аудио
        setMicMuted: (isMuted) => dispatch({ type: ACTION_TYPES.SET_MIC_MUTED, payload: isMuted }),
        setVoiceChatEnabled: (enabled) => dispatch({ type: ACTION_TYPES.SET_VOICE_CHAT_ENABLED, payload: enabled }),
        addRemoteStream: (id, stream) => dispatch({ type: ACTION_TYPES.ADD_REMOTE_STREAM, payload: { id, stream } }),
        removeRemoteStream: (id) => dispatch({ type: ACTION_TYPES.REMOVE_REMOTE_STREAM, payload: id }),
          // UI
        setError: (error) => dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error }),
        clearError: () => dispatch({ type: ACTION_TYPES.CLEAR_ERROR }),
        setInputRoomId: (roomId) => dispatch({ type: ACTION_TYPES.SET_INPUT_ROOM_ID, payload: roomId }),
        setShowAddUrlForm: (show) => dispatch({ type: ACTION_TYPES.SET_SHOW_ADD_URL_FORM, payload: show }),
        setShowConfirmModal: (show) => dispatch({ type: ACTION_TYPES.SET_SHOW_CONFIRM_MODAL, payload: show }),
        setConfirmModalProps: (props) => dispatch({ type: ACTION_TYPES.SET_CONFIRM_MODAL_PROPS, payload: props })
    }), [dispatch]);
};
